<template>
  <div>
    <form class="relative mt-10 flex flex-row items-end gap-2" @submit.prevent="submitPrompt">
      <ui-form-field class="grow">
        <template #label>
          <span class="h-5">{{ $t('conversations.filters.ai_search.label') }}</span>
          <template v-if="showResults && !isLoading">
            <div
              v-if="!getActiveFilterCount(dateGroupFilter) && aiFetchedFilterOptions?.length"
              class="flex items-center"
            >
              <alert-triangle-icon class="mr-2 h-4 w-4 text-yellow-60" />
              <div class="text-xs font-medium text-gray-70">{{ $t('filter.date_condition_warning') }}</div>
            </div>
          </template>
        </template>
        <intelligence-input
          v-model="aiFilterPrompt"
          placeholder='Try any prompt like "Conversations with negative sentiment created in the last 30 days"'
        />
      </ui-form-field>
      <button
        type="submit"
        class="absolute right-2 top-8 flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-10 text-gray-70 mix-blend-multiply transition-colors hover:bg-gray-20 hover:text-gray-80"
      >
        <arrow-right-icon class="" />
      </button>
    </form>
    <div class="mt-4 flex items-center gap-2">
      <span class="mr-2 text-xs font-medium">{{ $t('conversations.filters.ai_search.example_label') }}</span>
      <ui-tag theme="zennovate" tag="button" @click="() => useSuggestion('Tickets from last 30 days')">{{
        $t('conversations.filters.ai_search.example_1')
      }}</ui-tag>
      <ui-tag theme="zennovate" tag="button" @click="() => useSuggestion('Churn risk from last 2 weeks ')">
        {{ $t('conversations.filters.ai_search.example_2') }}
      </ui-tag>
      <ui-tag
        theme="zennovate"
        tag="button"
        @click="() => useSuggestion('Conversations with negative sentiment from last month')"
      >
        {{ $t('conversations.filters.ai_search.example_3') }}
      </ui-tag>
      <ui-tag tag="button" rounded class="ml-2 h-6 px-3 hover:bg-gray-40">
        {{ $t('conversations.filters.ai_search.more_examples') }}
      </ui-tag>
    </div>
    <div v-if="showResults || isLoading" class="mt-10">
      <ui-alert
        v-if="!explanationDismissed && data?.assistantResponse && !isLoading"
        compressed
        full-width
        closable
        class="mb-10"
        @close="explanationDismissed = true"
      >
        {{ data.assistantResponse }}
      </ui-alert>
      <div v-if="error" class="flex items-center justify-center p-2 text-xs text-gray-70">{{ error }}</div>
      <div v-else-if="isLoading" class="flex items-center justify-center gap-2 p-2">
        <ui-spinner inline no-spinner-background spinner-color="blue-40" />
        <span class="text-xs text-gray-70">{{ $t('conversations.filters.ai_search.loading') }}</span>
      </div>
      <div
        v-else-if="isFetched && !aiFetchedFilterOptions?.length"
        class="flex items-center justify-center p-2 text-xs text-gray-70"
      >
        {{ $t('conversations.filters.ai_search.no_results') }}
      </div>
      <div v-else-if="aiFetchedFilterOptions?.length">
        <div class="mb-2 text-xxs font-bold uppercase leading-4 text-gray-70">
          {{ $t('conversations.filters.ai_search.title') }}
        </div>
        <filter-group
          :added-filters="allPromptedFilters"
          :filter-options="filterOptions"
          @filter-adding-started="filterAddingStarted($event)"
          @filter-applied="filterApplied"
          @add-similar-filter="addSimilarFilter"
          @remove="removeFilter($event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { v4 as uuid } from 'uuid'
import { cloneDeep, debounce, isString } from 'lodash-es'
import { useQuery } from '@tanstack/vue-query'
import { UiFormField, UiSpinner, UiTag, UiAlert } from '@klausapp/ui-kit'
import { AlertTriangleIcon, ArrowRightIcon } from '@klausapp/ui-kit/icons'
import { toast } from '@/components/toast'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import useReviewSidebar from '@/composables/useReviewSidebar/useReviewSidebar'
import { type Option } from '@/components/pickers/TagPicker.vue'
import IntelligenceInput from '@/components/IntelligenceInput.vue'
import { searchTicketTags } from '@/api/ticket-tags'
import { SCALE_VALUES } from '@/data/scales'
import {
  type FilterItemEmit,
  type FilterOption,
  FilterValueType,
  GROUP,
  BOT,
  type ValueComponentProps,
} from '@/components/filters/types'

import FilterGroup from '@/components/filters/FilterGroup.vue'
import { isActiveFilter } from '@/components/filters/utils'
import autoInsightsFilters from '@/components/filters/autoInsightsFilters'
import useHelpdeskCustomFields from '@/modules/shared/filters/useHelpdeskCustomFields'

import { translationExists } from '@/i18n/utils'
import {
  convertValueToLabel,
  type DeconstructedOption,
  deconstructFilterOptions,
  type FilterOptionValue,
  type DropdownOption,
  isValidOption,
  translateUserConditionSpecialValueLabel,
  userConditions,
  botConditions,
} from '../../utils/filters'
import { type DefaultFilterOption, getFilterOptionValues } from '../../api/filters'
import { getAiFilterOptions } from '../../api/ai-filters'

import { hasIntelligenceIcon, hasBotIcon, dateGroupFilter } from './filterGrouping'

const props = defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  modelValue: DeconstructedOption[]
  editModal?: boolean
  isModal?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: DeconstructedOption[]): void
}>()

const { displayAsEmoji } = useReviewSidebar()

const aiFilterPrompt = ref('')
const aiFetchedFilterOptions = ref<DeconstructedOption[]>([])

const useSuggestion = (suggestion: string) => {
  aiFilterPrompt.value = suggestion
}

const showResults = ref(false)
const explanationDismissed = ref(false)
const promptedResultsCount = ref(0)
const val = ref<DeconstructedOption[]>([])
const fetchedValues = ref<Record<string, any[]>>({})

const {
  data,
  error,
  refetch,
  isFetching: isLoading,
  isFetched,
} = useQuery({
  queryKey: ['ai-filter-options'],
  queryFn: () => getAiFilterOptions(aiFilterPrompt.value),
  enabled: false,
  refetchOnMount: false,
})

watch(isFetched, (fetched) => {
  if (!fetched) return
  showResults.value = true
  explanationDismissed.value = false
  promptedResultsCount.value = aiFetchedFilterOptions.value.length
})

watch(aiFilterPrompt, () => {
  if (!aiFilterPrompt.value) showResults.value = false
})

async function submitPrompt() {
  if (!aiFilterPrompt.value) return
  try {
    await refetch()
    const { options, autoQaOptions } = data.value

    aiFetchedFilterOptions.value = deconstructFilterOptions({
      options: options || [],
      autoQaOptions: autoQaOptions || [],
      spotlightOptions: [],
      customCategoryOptions: [],
      intelligentFilterOptions: [],
      defaultOptions: props.options || [],
      defaultAutoQaOptions: props.autoqaFields || [],
      defaultSpotlightOptions: props.spotlightFields || [],
    })

    val.value = translateLabels(aiFetchedFilterOptions.value)
  } catch (err) {
    console.info('Error fetching AI filter options', err)
    toast({ status: 'error', message: 'Something went wrong' })
  }
}

const showAllConversationFilters = ref(!props.editModal)

watch(showAllConversationFilters, (curr, prev) => {
  if (prev && !curr) {
    val.value = val.value.filter((f) => isActiveFilter(f.values, f.condition))
  }
})

const workspaceIds = computed(() => [session.workspace.id])
const { fieldsMap } = useHelpdeskCustomFields(workspaceIds)

const getEmptyFilterOption = (): DeconstructedOption => ({
  id: uuid(),
  option: {
    name: '',
    label: i18n.t('conversations.filters.form.option_picker'),
  },
  condition: {},
  values: [],
})

const translateLabel = (opt) => {
  let label = ''
  if (opt?.isCustom && opt?.name) label = fieldsMap.value?.[opt.name]?.name || ''
  else if (opt?.label) label = translationExists(opt.label) ? i18n.t(opt.label) : opt.label
  return label
}

const getDescriptionKey = (opt: DeconstructedOption['option']) => {
  const optName = opt.name?.toLocaleLowerCase()
  const botQaFilters = ['related', 'reviewee']

  if (opt.isSpotlight) return 'filter.option.description.custom_spotlight'
  if (session.features.botQa && botQaFilters.includes(optName)) {
    return `filter.option.description.${optName}_with_bots`
  }

  return `filter.option.description.${optName}`
}
const getDescriptionOptions = (opt: DeconstructedOption['option']) => {
  return opt.isSpotlight ? [opt.customPhrases?.join(', ')] : null
}

const addLabel = (opt) => {
  if (!opt) return opt

  const descriptionKey = getDescriptionKey(opt)

  return {
    ...opt,
    label: translateLabel(opt),
    description: translationExists(descriptionKey) ? i18n.t(descriptionKey, getDescriptionOptions(opt)) : undefined,
  }
}

const translateLabels = (values: DeconstructedOption[]) => {
  return values.map((value) => ({
    ...value,
    option: addLabel(value.option),
    condition: addLabel(value.condition),
  }))
}

const vulnerabilityFilters = ['VULNERABILITY_HEALTH', 'VULNERABILITY_CAPABILITY', 'VULNERABILITY_LIFE_EVENT']

const removeFilter = (i: string) => {
  const index = val.value.findIndex(({ id }) => id === i)
  if (index === -1) return
  return val.value.splice(index, 1)
}

const filterApplied = (filterId: string, e: FilterItemEmit) => {
  const i = val.value.findIndex(({ id }) => id === filterId)
  if (!val.value[i]) return

  val.value[i].condition = e.selectedCondition

  if (
    getValueType(val.value[i]) === FilterValueType.Text &&
    (val.value[i].option.isCustom || val.value[i].option.isSpotlight)
  ) {
    if (val.value[i].values[0]) val.value[i].values[0].value = e.value
    else val.value[i].values[0] = { value: e.value }
  } else val.value[i].values = e.value
}

const addSimilarFilter = async (e: FilterItemEmit, filterOption: FilterOption) => {
  await filterAddingStarted(filterOption)
  const idIn = val.value[val.value.length - 1].id
  filterApplied(idIn, e)
}

const filterOptions = computed(() => {
  const optionCanBeSelected = (opt) =>
    !selectedOptionNames.value.includes(opt.name) || opt.allowMultiple || opt.isCustom || opt.isSpotlight

  // debugger
  return flatOptions.value.filter(optionCanBeSelected).map(addLabel)
})

const getFilterConditions = (option: DeconstructedOption['option']) => {
  let name = option ? option.name : ''
  if (!name) return []

  const flatOption = flatOptions.value.find((opt) => opt?.name === name)
  if (flatOption?.isCustom) name = 'TICKET_FIELD'

  let options = props.options

  if (flatOption?.isAutoQa) options = props.autoqaFields
  if (flatOption?.isSpotlight) options = props.spotlightFields

  return options
    .find((opt) => opt.filterOption === name || opt.name === name)
    ?.conditions.map((c) => ({
      name: c.condition,
      label: i18n.t(c.labelCode),
      valueRequired: c.valueRequired,
    }))
    .map(addLabel)
}

const getValueType = (d: DeconstructedOption): FilterValueType => {
  if (optionValuesLoading(d)) return FilterValueType.Loading
  if (!optionHasValues(d) && !d.option.isCustom) return FilterValueType.NoValues
  if (d.option.name === 'TICKET_TAGS' && Array.isArray(d.values)) return FilterValueType.SearchableTags
  if (d.option.isCustom) return FilterValueType.CustomField
  if (d.option.isSpotlight) return FilterValueType.NoValues
  if (!isString(getFilterValueType(d))) return FilterValueType.Generic
  if (getFilterValueType(d) === 'DATE') return FilterValueType.Date
  if (getFilterValueType(d) === 'INTEGER') return FilterValueType.Integer
  return FilterValueType.Text
}

const getValue = (d: DeconstructedOption) => {
  const valueType = getValueType(d)
  if (valueType === FilterValueType.Text && d.option.isCustom) return d.values[0]?.value
  if ([FilterValueType.Integer, FilterValueType.Text].includes(valueType)) {
    return Array.isArray(d.values) && d.values.length === 0 ? undefined : d.values
  }

  return d.values
}

const getValueComponentProps = (d: DeconstructedOption) => {
  const valueType = getValueType(d)

  if (valueType === FilterValueType.SearchableTags)
    return {
      apiMethod: getTicketTags,
    }

  if (valueType === FilterValueType.Generic)
    return {
      labelKey: ['TICKET_TAGS', 'SOURCE_TYPE'].includes(d.option.filterOption) ? 'value' : 'label',
      groups: getFilterValues(d),
      noResultsText: getValuePlaceholder(d),
    }
}

const getFilterValueType = (d: DeconstructedOption) => {
  const option = d.option

  if (option.name === 'TICKET_EXTERNAL_ID') return 'STRING'

  const value = option.name ? fetchedValues.value[option.name] : undefined
  return isString(value) ? value : []
}

const getFilterValues = (d: DeconstructedOption) => {
  const option = d.option

  if (option.isAutoQa) {
    const values = fetchedValues.value[option.name] || []
    const scale = SCALE_VALUES['SCALE_' + values.at(-1).value]

    const mappedValues = values.map((v, idx) => ({
      label: idx === 0 ? i18n.t('conversations.filters.auto_qa.value_na') : v.name,
      value: v.value,
      emojiSrc: displayAsEmoji.value && idx !== 0 && scale[idx - 1].emoji,
    }))

    return separateValues(mappedValues, d)
  }

  const valueOptions = option.name ? fetchedValues.value[option.name] : undefined

  if (isString(valueOptions)) return valueOptions
  if (!valueOptions) return []

  const keepValueFormat = option.name === 'TICKET_TAGS'
  const mappedValues = valueOptions.map((v) => {
    let label = v.label || (keepValueFormat ? v.value : convertValueToLabel(v.value))
    let value = v.value + ''

    if (!label && v.id && v.tag) {
      label = v.tag
      value = v.id.toString()
    }

    if (userConditions.includes(option.name)) {
      label = translateUserConditionSpecialValueLabel(v)
    }

    return { ...v, label, value }
  })

  return separateValues(mappedValues, d)
}

const getValuePlaceholder = (d: DeconstructedOption) => {
  const { option } = d
  if (option.name && fetchedValues.value && option.name in fetchedValues.value)
    return i18n.t('conversations.filters.form.value_empty')
  return i18n.t('conversations.filters.form.value_loading')
}

const optionHasValues = (d: DeconstructedOption) => {
  const { option } = d
  let name = option.name

  if (name && fetchedValues.value && name in fetchedValues.value) {
    const values = getFilterValues(d)

    if (Array.isArray(values)) {
      const flatValues = getFilterValues(d)
        .map((g) => g.options)
        .flat()
      return !!flatValues?.length
    } else if (values !== '') {
      return true
    }
  }

  return false
}

const optionValuesLoading = (d: DeconstructedOption) => {
  const { option } = d
  let name = option.name

  if (!name) return true

  return !(fetchedValues.value && name in fetchedValues.value)
}

const removeDeprecatedValues = (values, valValue: DeconstructedOption) => {
  if (Array.isArray(valValue.values)) {
    valValue.values = valValue.values?.filter(
      (v) => !values.includes(typeof v === 'string' ? v : v.value),
    ) as FilterOptionValue
  }
}

const getDefaultGroupTitle = (name: DeconstructedOption['option']['name']) => {
  if (userConditions.includes(name)) return i18n.t('conversations.filters.form.options.users')

  return ''
}

const separateValues = (values, d: DeconstructedOption) => {
  let vals = d.values

  const selectedValues = Array.isArray(vals) ? vals.map((v) => v.value) : [vals].map((v) => (v as any).value)
  const selectedName = d.option?.name || ''
  const hasSpecialValues = userConditions.includes(selectedName)
  const hasBots = botConditions.includes(selectedName)
  const customValues = hasSpecialValues ? values.filter((v) => v.value <= 3) : []
  const groupValues = hasSpecialValues ? values.filter((v) => v.type === GROUP) : []
  const botValues = hasSpecialValues ? values.filter((v) => v.type === BOT) : []

  // If some values are selected, place them first
  if (values.some((v) => selectedValues.includes(v.value))) {
    const matched = values
      .filter((v) => selectedValues.includes(v.value))
      .sort((a, b) => a.label.localeCompare(b.label))
    const matchedValues = matched.map((v) => v.value)
    const unmatchedValues = selectedValues.filter((v) => !matchedValues.includes(v))

    if (unmatchedValues.length) removeDeprecatedValues(unmatchedValues, d)

    const unselected = values.filter((v) => !selectedValues.includes(v.value))
    const filteredCustomValues = customValues.filter((v) => !selectedValues.includes(v.value))
    const filteredGroupValues = groupValues.filter((v) => !selectedValues.includes(v.value))
    const filteredBotValues = botValues.filter((v) => !selectedValues.includes(v.value))

    const groups = []

    if (matched.length) groups.push({ options: matched })
    if (filteredCustomValues.length) groups.push({ options: filteredCustomValues })
    if (hasBots && session.features.botQa) {
      groups.push({
        name: i18n.t('conversations.filters.form.options.bots'),
        options: filteredBotValues,
        emptyText: i18n.t('conversations.filters.form.options.bots_empty'),
      })
    }
    if (filteredGroupValues.length)
      groups.push({ name: i18n.t('conversations.filters.form.options.groups'), options: filteredGroupValues })
    const unselectedOptions = hasSpecialValues ? unselected.filter((v) => v.value > 3 && v.type !== GROUP) : unselected
    if (unselected.length) groups.push({ name: getDefaultGroupTitle(d.option.name), options: unselectedOptions })

    return groups
  }

  const groups = []

  if (customValues.length) groups.push({ options: customValues })
  if (hasBots && session.features.botQa) {
    groups.push({
      name: i18n.t('conversations.filters.form.options.bots'),
      options: botValues,
      emptyText: i18n.t('conversations.filters.form.options.bots_empty'),
    })
  }
  if (groupValues.length)
    groups.push({ name: i18n.t('conversations.filters.form.options.groups'), options: groupValues })

  const otherValues = hasSpecialValues ? values.filter((v) => v.value > 3 && v.type !== GROUP) : values
  if (otherValues.length) groups.push({ name: getDefaultGroupTitle(d.option.name), options: otherValues })

  return groups
}

const constructValueRequestParams = (option: DeconstructedOption['option']) => {
  const optionKey = option.name || ''
  let params: { option: string; source?: string; isAutoQa?: boolean; isSpotlight?: boolean } = { option: optionKey }

  if (option.isCustom) {
    const field = fieldsMap.value[option.name]
    params = { option: field?.externalId ?? '', source: field?.connectionSourceType }
  } else if (option.isAutoQa) params.isAutoQa = true
  else if (option.isSpotlight || optionKey === 'SPOTLIGHT_CUSTOM_PHRASE_SEARCH') params.isSpotlight = true

  return params
}

const filterAddingStarted = async (obj: any) => {
  val.value.push(getEmptyFilterOption())

  const i = val.value.length - 1
  val.value[i].option = obj
  resetFields(i)
  await loadOptionValues(val.value[i].option)
}

const loadOptionValues = async (option: DeconstructedOption['option']) => {
  // Ticket tags and custom fields are handled by a separate component
  if (option.name === 'TICKET_TAGS' || option.isCustom) {
    fetchedValues.value[option.name] = [1]
    return []
  } else if (option.isSpotlight) {
    fetchedValues.value[option.name] = [option.label]
    return []
  }

  const params = constructValueRequestParams(option)
  const { data } = await getFilterOptionValues(
    params.option,
    params.isAutoQa || params.isSpotlight || params.option === 'SPOTLIGHT_CUSTOM_PHRASE_SEARCH' ? 'autoqa' : 'default',
  )

  if (params.option) fetchedValues.value[params.option] = data

  return data
}

const resetFields = (i: number) => {
  const emptyRow = getEmptyFilterOption()
  val.value[i].condition = emptyRow.condition
  val.value[i].values = emptyRow.values
}

const isDowngradedFilter = (option) => {
  return (
    (option.name === 'CONVERSATION_SENTIMENT' && !session.features.sentiment) ||
    (option.name === 'DISPUTE_STATUS' && !session.features.disputes) ||
    (autoInsightsFilters.includes(option.name) && !session.features.autoInsights) ||
    (option.isAutoQa && !session.features.autoQa)
  )
}

const debouncedEmit = debounce((val) => {
  emit('update:modelValue', cloneDeep(val))
}, 300)

// We can't assume that the label of the filtername coming from the backend is the same
// as the i18n key, so we need to map it here.
// This is because every time you want to change a string, you have to create a new i18n key
// (rather than just changing the value of the existing key) & it doesn't make sense to change
// the BE key every time we want to edit the wording in the UI.
const filterOptionNameToI18nKey = {
  TRANSCRIPTION_CONTENT: 'filter.option.label.transcription_content_v2',
  SLA_BREACH: 'filter.option.label.sla_breach_v2',
}

const flatOptions = computed(() => {
  const defaultOptions: DropdownOption[] = props.options
    .filter(
      (opt) =>
        opt.filterOption !== 'TICKET_FIELD' &&
        (session.features.sentiment || opt.filterOption !== 'CONVERSATION_SENTIMENT') &&
        (session.features.disputes || opt.filterOption !== 'DISPUTE_STATUS') &&
        (session.features.vulnerabilityFilters || !vulnerabilityFilters.includes(opt.filterOption)) &&
        (session.features.callTranscriptions ||
          session.features.talkTranscriptionsEnabled ||
          opt.filterOption !== 'TRANSCRIPTION_CONTENT') &&
        (session.features.autoInsights || !autoInsightsFilters.includes(opt.filterOption)),
    )
    .map((opt) => ({
      allowMultiple: opt.allowedMultiple,
      name: opt.filterOption,
      label: i18n.t(filterOptionNameToI18nKey[opt.filterOption] || opt.labelCode),
      isCustom: false as const,
      isAutoQa: false as const,
      isSpotlight: false as const,
      isCustomCategory: false as const,
      intelligenceIcon: hasIntelligenceIcon.includes(opt.filterOption),
      botIcon: hasBotIcon(opt),
    }))

  const autoQaOptions: DropdownOption[] = (session.features.autoQa ? props.autoqaFields : []).map((opt) => {
    return {
      allowMultiple: opt.allowedMultiple,
      name: opt.filterOption,
      label: i18n.t(opt.labelCode),
      isCustom: false as const,
      isAutoQa: true as const,
      isSpotlight: false as const,
      isCustomCategory: false as const,
      intelligenceIcon: true,
    }
  })
  const spotlightOptions: DropdownOption[] = props.spotlightFields.map((opt) => {
    return {
      allowMultiple: opt.allowedMultiple,
      customPhrases: opt.customPhrases,
      label: opt.name,
      name: opt.name,
      id: opt.id,
      intelligenceIcon: true,
      isAutoQa: false as const,
      isCustom: false as const,
      isSpotlight: true as const,
      isCustomCategory: false as const,
    }
  })
  const customFields: DropdownOption[] = Object.values(fieldsMap.value || {}).map((opt) => ({
    name: opt.externalId,
    label: opt.name,
    isCustom: true as const,
    isAutoQa: false as const,
    isSpotlight: false as const,
    isCustomCategory: false as const,
    allowMultiple: false,
  }))
  const uniqueCustomFields = [...new Map(customFields.map((item) => [item.name, item])).values()]
  const options = [...defaultOptions, ...uniqueCustomFields, ...autoQaOptions, ...spotlightOptions]

  return options.sort((a, b) => a.label.localeCompare(b.label))
})

const selectedOptionNames = computed(() => {
  return val.value.map((filter) => filter.option.name)
})

const validOptions = computed(() => {
  return val.value.filter(isValidOption)
})

watch(validOptions, (val) => debouncedEmit(val), { deep: true })

watch(
  () => val.value,
  (newVal, oldVal) => {
    if (!newVal?.length || oldVal?.length) return

    newVal.filter((row) => row.option.name && row.values).map((row) => loadOptionValues(row.option))
  },
  {
    immediate: true,
  },
)

const getTicketTags = async (tag: string): Promise<{ tags: Option[]; total: number }> => {
  const { tags, total } = await searchTicketTags({
    workspaceIds: [session.workspace.id],
    tag,
  })
  return { tags: tags.map((t) => ({ label: t, value: t })), total }
}

const getFilterGroupItems = (filterFn) => {
  return val.value
    .filter(({ option }) => filterFn(option))
    .map((d) => {
      const descriptionKey = getDescriptionKey(d.option)

      return {
        id: d.id,
        label: d.option.label,
        name: d.option.name,
        showAddSimilarButton: !!filterOptions.value.find((o) => o?.name === d.option.name),
        description: translationExists(descriptionKey)
          ? i18n.t(descriptionKey, getDescriptionOptions(d.option))
          : undefined,
        selectedCondition: d.condition,
        conditions: getFilterConditions(d.option),
        value: getValue(d),
        valueType: getValueType(d),
        valueComponentProps: getValueComponentProps(d) as ValueComponentProps,
        intelligenceIcon: hasIntelligenceIcon.includes(d.option.name) || d.option.isAutoQa || d.option.isSpotlight,
        botIcon: hasBotIcon(d.option),
        disabled: isDowngradedFilter(d.option),
      }
    })
}

const getActiveFilterCount = (groupFilter) => {
  return getFilterGroupItems(groupFilter).filter((v) => isActiveFilter(v.value, v.selectedCondition)).length
}

const allPromptedFilters = computed(() => {
  return getFilterGroupItems(() => true)
})
</script>
